/**
 * Card-backed trial whose single analysis is already used.
 *
 * These users have already chosen a plan: it starts (and converts) at
 * `plan_starts_at`. The backend blocks further analyses with a structured
 * detail `{ code: 'trial_analysis_used', message, plan_starts_at }` (HTTP)
 * or the job error code `trial_analysis_used` (worker). Every surface that
 * shows an analysis-dispatch error routes through `trialAnalysisUsedMessage`
 * so the copy stays "more analyses unlock when your plan starts", never a
 * "choose a plan" call to action.
 */
import { formatServerDate } from './serverDate';

export const TRIAL_ANALYSIS_USED_CODE = 'trial_analysis_used';

export interface TrialAnalysisUsedInfo {
  planStartsAt: string | null;
}

type Translate = (key: string, fallbackOrOptions?: any, options?: any) => string;

type AnyRecord = Record<string, any>;

function asRecord(value: unknown): AnyRecord | null {
  return value && typeof value === 'object' ? (value as AnyRecord) : null;
}

function fromDetail(detail: unknown): TrialAnalysisUsedInfo | null {
  const record = asRecord(detail);
  if (!record || record.code !== TRIAL_ANALYSIS_USED_CODE) return null;
  return { planStartsAt: typeof record.plan_starts_at === 'string' ? record.plan_starts_at : null };
}

/**
 * Detect the block on any error shape the app produces: errors built by
 * `apiErrorFromBody` / apiClient (`.detail`), axios errors
 * (`.response.data.detail`), a raw response body, or job/WebSocket payloads
 * (`errorCode` / `error_code`).
 */
export function getTrialAnalysisUsed(error: unknown): TrialAnalysisUsedInfo | null {
  const record = asRecord(error);
  if (!record) return null;
  // Nested details first: an Error from apiErrorFromBody also carries `.code`
  // on itself, but only its `.detail` has `plan_starts_at`.
  const candidates = [record.detail, record.response?.data?.detail, record.response?.data, record];
  for (const candidate of candidates) {
    const info = fromDetail(candidate);
    if (info) return info;
  }
  if (record.errorCode === TRIAL_ANALYSIS_USED_CODE || record.error_code === TRIAL_ANALYSIS_USED_CODE) {
    return { planStartsAt: typeof record.planStartsAt === 'string' ? record.planStartsAt : null };
  }
  return null;
}

/** Localized copy; the date is omitted when unknown or unparseable. */
export function formatTrialAnalysisUsedMessage(
  t: Translate,
  language: string,
  planStartsAt: string | null | undefined,
): string {
  const date = formatServerDate(planStartsAt ?? null, language);
  return date
    ? t('trial.analysisUsed.message', { date })
    : t('trial.analysisUsed.messageNoDate');
}

/**
 * The localized message when `error` is the trial-analysis-used block, else
 * null (callers keep their existing message). `fallbackPlanStartsAt` (e.g.
 * the trial status `expires_at`) fills the date when the error itself has
 * none, as with worker-side job failures.
 */
export function trialAnalysisUsedMessage(
  t: Translate,
  language: string,
  error: unknown,
  fallbackPlanStartsAt?: string | null,
): string | null {
  const info = getTrialAnalysisUsed(error);
  if (!info) return null;
  return formatTrialAnalysisUsedMessage(t, language, info.planStartsAt || fallbackPlanStartsAt);
}

export type ApiDetailError = Error & { status?: number; detail?: unknown; code?: string };

/**
 * Build the Error thrown for a non-OK fetch response. A string `detail`
 * stays the message (unchanged behaviour); a structured `{ code, message }`
 * detail keeps its message as text and stays inspectable on `.detail`
 * instead of collapsing to "[object Object]".
 */
export function apiErrorFromBody(body: unknown, status: number | undefined, fallback: string): ApiDetailError {
  const record = asRecord(body);
  const detail = record && 'detail' in record ? record.detail : undefined;
  const detailRecord = asRecord(detail);
  let message: string = fallback;
  if (typeof detail === 'string' && detail) {
    message = detail;
  } else if (detailRecord && typeof detailRecord.message === 'string' && detailRecord.message) {
    message = detailRecord.message;
  } else if (record && typeof record.message === 'string' && record.message) {
    message = record.message;
  }
  const error = new Error(message) as ApiDetailError;
  if (status !== undefined) error.status = status;
  if (detail !== undefined) error.detail = detail;
  if (detailRecord && typeof detailRecord.code === 'string') error.code = detailRecord.code;
  return error;
}
